import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import './App.css';
import { supabase } from './supabaseClient';
import defaultUser from './user-default.png';
import UserProfile from './UserProfile';

function MentorConnections({ setPage, setCallParams, theme = 'light' }) {
  const [userId, setUserId] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [tab, setTab] = useState('pending');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [viewingUserId, setViewingUserId] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (user) setUserId(user.uid);
      else setUserId(null);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const q = query(collection(db, 'mentorRequests'), where('mentorId', '==', userId));
    const unsubscribe = onSnapshot(q, async snapshot => {
      const pending = [];
      const accepted = [];
      const rejected = [];
      const availabilityPromises = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const requesterId = data.requesterId;

        const requesterDoc = await getDoc(doc(db, 'users', requesterId));
        if (!requesterDoc.exists()) continue;
        const menteeData = requesterDoc.data();
        const menteeName = menteeData.fullName || menteeData.email || 'Unknown';

        const { data: supa } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(`profile-pictures/${requesterId}/profile.jpg`);
        const photoURL = supa?.publicUrl || defaultUser;

        const availPromise = getDoc(doc(db, 'availability', requesterId)).then(availDoc => {
          if (availDoc.exists()) return availDoc.data();
          return null;
        });

        availabilityPromises.push(availPromise.then(availabilityData => {
          const item = {
            chatId: docSnap.id,
            menteeName,
            photoURL,
            status: data.status,
            createdAt: data.createdAt?.toDate?.() || null,
            availability: availabilityData,
            menteeId: requesterId,
          };

          if (data.status === 'accepted') accepted.push(item);
          else if (data.status === 'pending') pending.push(item);
          else if (data.status === 'rejected') rejected.push(item);
        }));
      }

      await Promise.all(availabilityPromises);

      setPendingRequests(pending);
      setAcceptedRequests(accepted);
      setRejectedRequests(rejected);
    });

    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!userId || acceptedRequests.length === 0) return;

    const unsubscribes = acceptedRequests.map(chat => {
      const messagesRef = collection(db, 'mentorRequests', chat.chatId, 'messages');
      return onSnapshot(messagesRef, snapshot => {
        let unread = 0;
        snapshot.forEach(doc => {
          const msg = doc.data();
          if (msg.sender !== userId && (!msg.seenBy || !msg.seenBy.includes(userId))) {
            unread++;
          }
        });
        setUnreadCounts(prev => ({ ...prev, [chat.chatId]: unread }));
      });
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [userId, acceptedRequests]);

  const acceptRequest = async (chatId) => {
    try {
      await updateDoc(doc(db, 'mentorRequests', chatId), { status: 'accepted' });
      alert('Request accepted.');
    } catch (err) {
      console.error(err);
      alert('Failed to accept request.');
    }
  };

  const rejectRequest = async (chatId) => {
    if (!window.confirm('Reject this mentee request?')) return;
    try {
      await updateDoc(doc(db, 'mentorRequests', chatId), { status: 'rejected' });
      alert('Request rejected.');
    } catch (err) {
      console.error(err);
      alert('Failed to reject request.');
    }
  };

  const deleteChat = async (chatId) => {
    if (!window.confirm('Delete this chat request?')) return;
    try {
      await deleteDoc(doc(db, 'mentorRequests', chatId));
      alert('Deleted successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to delete.');
    }
  };

  if (viewingUserId) {
    return (
      <UserProfile
        userId={viewingUserId}
        setPage={() => setViewingUserId(null)}
        theme={theme}
      />
    );
  }

  if (!userId) return <p style={{ padding: 20, color: theme === 'dark' ? '#eee' : '#333' }}>Please log in to view your mentee connections.</p>;

  const colors = {
    light: {
      background: '#f0f4f8',
      cardBackground: '#ffffff',
      textPrimary: '#222',
      textSecondary: '#555',
      borderColor: '#e2e8f0',
      btnPrimaryBg: 'linear-gradient(135deg, #1d8cf8, #3358f4)',
      btnPrimaryColor: '#fff',
      btnSecondaryBg: '#e2e8f0',
      btnSecondaryColor: '#333',
      btnDangerBg: '#ff5e5e',
      btnDangerColor: '#fff',
      shadow: 'rgba(0,0,0,0.1)',
      unreadBadgeBg: '#ff4757',
      unreadBadgeColor: '#fff',
    },
    dark: {
      background: '#121212',
      cardBackground: '#1e1e1e',
      textPrimary: '#eee',
      textSecondary: '#bbb',
      borderColor: '#333',
      btnPrimaryBg: 'linear-gradient(135deg, #0d6efd, #6610f2)',
      btnPrimaryColor: '#fff',
      btnSecondaryBg: '#2c2c2c',
      btnSecondaryColor: '#eee',
      btnDangerBg: '#b02a37',
      btnDangerColor: '#fff',
      shadow: 'rgba(0,0,0,0.7)',
      unreadBadgeBg: '#ff4757',
      unreadBadgeColor: '#fff',
    },
  };

  const c = colors[theme];

  const Section = ({ title, children }) => (
    <section style={{ marginBottom: 48 }}>
      <h3 style={{
        color: c.textPrimary,
        paddingBottom: 12,
        fontWeight: 700,
        fontSize: '1.75rem',
        borderLeft: `6px solid ${c.btnPrimaryColor}`,
        paddingLeft: 12,
        userSelect: 'none',
      }}>{title}</h3>
      {children}
    </section>
  );

  const cardStyle = {
    backgroundColor: c.cardBackground,
    borderRadius: 16,
    padding: 24,
    boxShadow: `0 8px 20px ${c.shadow}`,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '48px 32px',
      backgroundColor: c.background,
      color: c.textPrimary,
      fontFamily: "'Inter', sans-serif",
      maxWidth: 1200,
      margin: '0 auto',
      userSelect: 'none',
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: 40, fontWeight: 800 }}>Mentee Connections</h1>

      <Section title="Accepted Mentees">
        {acceptedRequests.length ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 24,
          }}>
            {acceptedRequests.map(r => (
              <div
                key={r.chatId}
                onClick={() => setViewingUserId(r.menteeId)}
                style={{
                  ...cardStyle,
                  position: 'relative',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = `0 12px 28px ${c.shadow}`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${c.shadow}`;
                }}
                title="Click to view mentee profile"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') setViewingUserId(r.menteeId); }}
              >
                <img
                  src={r.photoURL}
                  alt={r.menteeName}
                  style={{
                    width: 90,
                    height: 90,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginBottom: 16,
                    border: `2px solid ${c.btnPrimaryColor}`,
                  }}
                  loading="lazy"
                />
                <div style={{ fontWeight: 700, fontSize: '1.15rem', marginBottom: 6 }}>{r.menteeName}</div>
                {(unreadCounts[r.chatId] || 0) > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    backgroundColor: c.unreadBadgeBg,
                    color: c.unreadBadgeColor,
                    borderRadius: '50%',
                    fontSize: 14,
                    fontWeight: 700,
                    padding: '6px 10px',
                    boxShadow: `0 0 6px ${c.unreadBadgeBg}`,
                  }}>
                    {unreadCounts[r.chatId]}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: c.textSecondary }}>No accepted mentees yet.</p>
        )}
      </Section>

      <nav style={{ display: 'flex', gap: 16, marginBottom: 36 }}>
        {['pending', 'rejected'].map(type => (
          <button
            key={type}
            onClick={() => setTab(type)}
            style={{
              backgroundColor: tab === type ? c.btnPrimaryBg : c.btnSecondaryBg,
              color: tab === type ? c.btnPrimaryColor : c.btnSecondaryColor,
              border: 'none',
              padding: '14px 28px',
              borderRadius: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
            }}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </nav>

      {tab === 'pending' && (
        <Section title="Pending Requests">
          {pendingRequests.length ? (
            <div style={{ display: 'grid', gap: 24 }}>
              {pendingRequests.map(r => (
                <div key={r.chatId} style={{
                  ...cardStyle,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>{r.menteeName}</div>
                  <div style={{ fontSize: 13, color: c.textSecondary, marginBottom: 16 }}>
                    Request ID: {r.chatId.slice(0, 6)}
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={() => acceptRequest(r.chatId)}
                      style={{
                        flexGrow: 1,
                        background: c.btnPrimaryBg,
                        color: c.btnPrimaryColor,
                        borderRadius: 10,
                        padding: '12px 0',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'transform 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => rejectRequest(r.chatId)}
                      style={{
                        flexGrow: 1,
                        backgroundColor: c.btnSecondaryBg,
                        color: c.btnSecondaryColor,
                        borderRadius: 10,
                        padding: '12px 0',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'transform 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p style={{ color: c.textSecondary }}>No pending requests.</p>}
        </Section>
      )}

      {tab === 'rejected' && (
        <Section title="Rejected Requests">
          {rejectedRequests.length ? (
            <div style={{ display: 'grid', gap: 24 }}>
              {rejectedRequests.map(r => (
                <div key={r.chatId} style={cardStyle}>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>{r.menteeName}</div>
                  <div style={{ fontSize: 13, color: c.textSecondary, marginBottom: 16 }}>
                    Request ID: {r.chatId.slice(0, 6)}
                  </div>
                  <button
                    onClick={() => deleteChat(r.chatId)}
                    style={{
                      backgroundColor: c.btnDangerBg,
                      color: c.btnDangerColor,
                      borderRadius: 10,
                      padding: '12px 0',
                      fontWeight: 600,
                      cursor: 'pointer',
                      width: '100%',
                      transition: 'transform 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : <p style={{ color: c.textSecondary }}>No rejected requests.</p>}
        </Section>
      )}
    </div>
  );
}

export default MentorConnections;
